import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button, Skeleton } from "@nexus/ui";
import type { ChangelogSettings } from "@/features/changelog/api/changelog.api";
import { changelogSettingsQueryOptions } from "@/lib/queries";
import { useSaveChangelogSettings } from "@/features/changelog/hooks/useChangelogActions";
import { useChangelogPermissions } from "@/features/changelog/hooks/useChangelogPermissions";
import { changelogErrorMessage } from "@/features/changelog/lib/changelog-errors";
import {
  applyPreset,
  changedSettings,
  toDraft,
  type ChangelogAutomationPreset,
  type ChangelogSettingsDraft,
} from "@/features/changelog/lib/changelog-settings";
import {
  changelogGroupsWithChanges,
  CHANGELOG_SETTINGS_GROUP_META,
  CHANGELOG_SETTINGS_GROUPS,
  type ChangelogSettingsGroup,
} from "@/features/changelog/lib/changelog-settings-groups";
import { ChangelogNotice } from "./shared/ChangelogNotice";
import { SettingsSaveFooter } from "./shared/SettingsCard";
import {
  SettingsRail,
  settingsRailPanelId,
  settingsRailTabId,
} from "./shared/SettingsRail";
import { AutomationPresetCard } from "./settings/AutomationPresetCard";
import { ReleasePostsCard } from "./settings/ReleasePostsCard";
import { ReleaseNotesCard } from "./settings/ReleaseNotesCard";
import { RoundupsCard } from "./settings/RoundupsCard";
import { SprintsCard } from "./settings/SprintsCard";
import { SyncingAlertsCard } from "./settings/SyncingAlertsCard";
import { UserFacingCard } from "./settings/UserFacingCard";
import type { ChangelogSettingsCardProps } from "./settings/types";

/** Namespaces the rail's tab and panel ids. */
const RAIL_ID = "changelog-settings";

interface ChangelogSettingsPageProps {
  group: ChangelogSettingsGroup;
  onGroupChange: (group: ChangelogSettingsGroup) => void;
}

/**
 * How the release pipeline behaves.
 *
 * It is one saved record shown a group at a time, opening on the only question
 * most admins have — how automatic should this be. Everything else is the
 * machinery that question sets, split by what it governs: what reaches the
 * team's Slack, what a roundup does on its own, how the notes read, and how
 * releases are noticed.
 *
 * The draft and the save footer live here rather than in the cards, so an edit
 * survives a move between groups and one Save button covers all of them.
 */
export function ChangelogSettingsPage({ group, onGroupChange }: ChangelogSettingsPageProps) {
  const { t, i18n } = useTranslation();
  const permissions = useChangelogPermissions();

  const { data, isPending, isError, refetch } = useQuery({
    ...changelogSettingsQueryOptions(),
    enabled: permissions.canViewSettings,
  });

  const save = useSaveChangelogSettings();

  const [draft, setDraft] = useState<ChangelogSettingsDraft | null>(null);
  // Re-seeding during render rather than in an effect keeps the form and the
  // server copy in the same commit: an effect would paint one frame of the old
  // draft against the new baseline, which reads as the save being undone.
  const [baseline, setBaseline] = useState<ChangelogSettings | null>(null);
  if (data && data !== baseline) {
    setBaseline(data);
    setDraft(toDraft(data));
  }

  if (!permissions.canViewSettings) {
    return (
      <p className="m-0 max-w-[68ch] text-xs text-muted-foreground">
        {t("admin.changelog.settings.noAccess", "You do not have access to release settings.")}
      </p>
    );
  }

  // Checked before the loading state: a failed fetch leaves `data` undefined
  // too, and skeletons that never resolve would be the only thing on screen.
  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="m-0 text-sm text-muted-foreground">
          {t("admin.changelog.settings.loadError", "Could not load release settings.")}
        </p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          {t("admin.changelog.retry", "Try again")}
        </Button>
      </div>
    );
  }

  if (isPending || !draft || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const changes = changedSettings(draft, data);
  const changedCount = Object.keys(changes).length;
  const isReadOnly = !permissions.canEditSettings;

  function set<K extends keyof ChangelogSettingsDraft>(key: K, value: ChangelogSettingsDraft[K]) {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  }

  function handleSave() {
    if (changedCount === 0) return;
    save.mutate(changes, {
      onSuccess: () =>
        toast.success(t("admin.changelog.settings.savedToast", "Release settings saved")),
    });
  }

  const isQuietWindowEmpty =
    draft.quietHoursEnabled && draft.quietHoursStartMinute === draft.quietHoursEndMinute;
  const canSave = changedCount > 0 && !isQuietWindowEmpty && !save.isPending;

  const cardProps: ChangelogSettingsCardProps = {
    draft,
    set,
    isReadOnly,
    isSlackConfigured: data.slackConfigured,
    isAlertsSlackConfigured: data.alertsSlackConfigured,
  };

  const changedGroups = changelogGroupsWithChanges(Object.keys(changes));
  const railItems = CHANGELOG_SETTINGS_GROUPS.map((id) => ({
    id,
    icon: CHANGELOG_SETTINGS_GROUP_META[id].icon,
    label: t(
      CHANGELOG_SETTINGS_GROUP_META[id].labelKey,
      CHANGELOG_SETTINGS_GROUP_META[id].labelFallback,
    ),
    isChanged: changedGroups.has(id),
  }));

  const savedCredit =
    data.updatedAt && data.updatedByName
      ? t("admin.changelog.settings.credit", "Last changed by {{by}} on {{at}}", {
          by: data.updatedByName,
          at: new Date(data.updatedAt).toLocaleDateString(i18n.language, {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
        })
      : t("admin.changelog.settings.creditDefaults", "Nobody has changed these yet.");

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row md:gap-6">
        <SettingsRail
          idPrefix={RAIL_ID}
          label={t("admin.changelog.settings.groups.label", "Release settings groups")}
          unsavedLabel={t("admin.changelog.settings.groups.unsaved", "Unsaved changes")}
          items={railItems}
          active={group}
          onChange={onGroupChange}
        />

        <div
          role="tabpanel"
          id={settingsRailPanelId(RAIL_ID, group)}
          aria-labelledby={settingsRailTabId(RAIL_ID, group)}
          className="min-h-0 flex-1 space-y-4 overflow-auto pb-2"
        >
          {isReadOnly ? (
            <ChangelogNotice
              title={t(
                "admin.changelog.settings.readOnlyTitle",
                "You can read these, not change them",
              )}
              description={t(
                "admin.changelog.settings.readOnly",
                "Ask an admin with the changelog settings permission to make the change.",
              )}
            />
          ) : null}

          <GroupCards
            group={group}
            cardProps={cardProps}
            onApplyPreset={(preset) =>
              setDraft((current) => (current ? applyPreset(current, preset) : current))
            }
          />
        </div>
      </div>

      {/* Kept out of the scroll area: a save button that scrolls away is a save
          button somebody does not press. It counts changes across every group,
          which is why the rail marks the groups they are sitting on. */}
      <SettingsSaveFooter
        status={
          save.error
            ? changelogErrorMessage(
                save.error,
                t("admin.changelog.settings.saveError", "Could not save these settings."),
              )
            : changedCount > 0
              ? t("admin.changelog.settings.dirty", { count: changedCount })
              : savedCredit
        }
      >
        {isReadOnly ? null : (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 rounded-sm text-xs"
              disabled={changedCount === 0 || save.isPending}
              onClick={() => {
                setDraft(toDraft(data));
                save.reset();
              }}
            >
              {t("admin.changelog.settings.discard", "Discard")}
            </Button>
            <Button
              size="sm"
              className="h-8 rounded-sm text-xs"
              disabled={!canSave}
              onClick={handleSave}
            >
              {save.isPending
                ? t("admin.changelog.settings.saving", "Saving…")
                : t("admin.changelog.settings.save", "Save changes")}
            </Button>
          </>
        )}
      </SettingsSaveFooter>
    </div>
  );
}

interface GroupCardsProps {
  group: ChangelogSettingsGroup;
  cardProps: ChangelogSettingsCardProps;
  onApplyPreset: (preset: ChangelogAutomationPreset) => void;
}

/** The cards on one panel. A group is free to hold more than one. */
function GroupCards({ group, cardProps, onApplyPreset }: GroupCardsProps) {
  switch (group) {
    case "automation":
      return <AutomationPresetCard {...cardProps} onApplyPreset={onApplyPreset} />;
    case "releases":
      return <ReleasePostsCard {...cardProps} />;
    case "roundups":
      return (
        <>
          <RoundupsCard {...cardProps} />
          <SprintsCard {...cardProps} />
        </>
      );
    case "notes":
      return (
        <>
          <ReleaseNotesCard {...cardProps} />
          <UserFacingCard {...cardProps} />
        </>
      );
    case "ops":
      return <SyncingAlertsCard {...cardProps} />;
  }
}
